/**
 * tenteco API クラス
 * 環境に応じたベースURL管理とAPI呼び出し機能を提供
 */
class TentecoAPI {
  constructor() {
    this.environments = {
      local: {
        hostname: ['127.0.0.1', 'localhost'],
        baseUrl: 'https://webapp.fuel.noren.io/v1',
        name: 'ローカル開発'
      },
      staging: {
        hostname: ['staging-tenteco.myshopify.com'],
        baseUrl: 'https://webapp.service-stg.tenteco.jp/v1',
        name: 'ステージング'
      },
      production: {
        hostname: ['tenteco.jp'],
        baseUrl: 'https://webapp.service.tenteco.jp/v1',
        name: '本番'
      },
      development: {
        hostname: ['development-tenteco.myshopify.com'],
        baseUrl: 'https://webapp.fuel.noren.io/v1',
        name: '開発'
      }
    };
    
    this.currentEnvironment = this.detectEnvironment();
  }

  /**
   * 現在の環境を検出
   * @returns {Object} 環境情報
   */
  detectEnvironment() {
    const hostname = window.location.hostname;
    
    for (const [key, env] of Object.entries(this.environments)) {
      if (env.hostname.includes(hostname)) {
        return {
          key: key,
          ...env
        };
      }
    }
    
    // デフォルトは開発環境
    return {
      key: 'development',
      ...this.environments.development
    };
  }

  /**
   * 現在の環境に応じたベースURLを取得
   * @returns {string} ベースURL
   */
  getBaseUrl() {
    return this.currentEnvironment.baseUrl;
  }

  /**
   * 現在の環境情報を取得
   * @returns {Object} 環境情報
   */
  getEnvironmentInfo() {
    return {
      hostname: window.location.hostname,
      baseUrl: this.currentEnvironment.baseUrl,
      environment: this.currentEnvironment.name,
      environmentKey: this.currentEnvironment.key
    };
  }

  /**
   * 環境を手動で設定
   * @param {string} environmentKey 環境キー
   */
  setEnvironment(environmentKey) {
    if (this.environments[environmentKey]) {
      this.currentEnvironment = {
        key: environmentKey,
        ...this.environments[environmentKey]
      };
    } else {
      console.warn(`無効な環境キー: ${environmentKey}`);
    }
  }

  /**
   * Tenteco APIのpingエンドポイントにGETリクエストを送信
   * @returns {Promise<Object>} APIレスポンス
   */
  async ping() {
    try {
      const url = `${this.getBaseUrl()}/ping`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Tenteco API ping リクエストエラー:', error);
      return false;
    }
  }

  /**
   * Tenteco APIのsteel-category-setエンドポイントにGETリクエストを送信
   * @returns {Promise<Object>} APIレスポンス
   */
  async getSteelCategorySet() {
    try {
      const url = `${this.getBaseUrl()}/steel-category-set`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Tenteco API steel-category-set リクエストエラー:', error);
      return { varieties: [] };
    }
  }

  /**
   * Tenteco APIのsteel-search-fieldsエンドポイントにGETリクエストを送信
   * @returns {Promise<Object>} APIレスポンス
   */
  async getSteelSearchFields() {
    try {
      const url = `${this.getBaseUrl()}/steel-search-fields`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Tenteco API steel-search-fields リクエストエラー:', error);
      return { fields: [] };
    }
  }

  /**
   * Tenteco APIのsteel-searchエンドポイントにPOSTリクエストを送信
   * @returns {Promise<Object>} APIレスポンス
   * @param {Object} params 送信データ
   */
  async postSteelSearch(params) {
    try {
      const url = `${this.getBaseUrl()}/steel-search`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Tenteco API steel-search リクエストエラー:', error);
      return { results: [], total_count: 0 };
    }
  }

  /**
   * カスタムエンドポイントにGETリクエストを送信
   * @param {string} endpoint エンドポイント（例: '/users', '/products'）
   * @returns {Promise<Object>} APIレスポンス
   */
  async get(endpoint) {
    try {
      const url = `${this.getBaseUrl()}${endpoint}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Tenteco API GET リクエストエラー (${endpoint}):`, error);
      return null;
    }
  }

  /**
   * カスタムエンドポイントにPOSTリクエストを送信
   * @param {string} endpoint エンドポイント
   * @param {Object} data 送信データ
   * @returns {Promise<Object>} APIレスポンス
   */
  async post(endpoint, data) {
    try {
      const url = `${this.getBaseUrl()}${endpoint}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error(`Tenteco API POST リクエストエラー (${endpoint}):`, error);
      return null;
    }
  }
}

// インスタンスを作成
const tentecoAPI = new TentecoAPI();

// グローバルスコープで利用可能にする
window.TentecoAPI = TentecoAPI;
window.tentecoAPI = tentecoAPI;
